import React from 'react'
import * as jsjoda from '@js-joda/core'
import { getGendersEnum } from '_services/enums.service'
import { updateProfile } from '_services/users.service'
import { isAxiosError } from 'axios'
import { useNavigate } from 'react-router-dom'
import useCurrentUser from '../../../../_core/contexts/currentUser'
import { PopUpContextType, PopupContext } from '../../../../_core/contexts/popToast'
import { useFetchV2, useEnumOptions, useLocation } from '../../../../_core/hooks'
import { useGeocoding, geocodeCountry } from '../../../../_core/utility/geocoding'
import { EnumUserGendersData, genderOptions, genderPreferenceOptions } from 'types/enums.options'
import { OnboardingForm, FormType, IncomingFormPayload } from 'types/payloads.users'
import { UserQuestionResponse, UserPhoto } from 'types/user'
import { getValidationErrors } from 'util/validation.messages'
import EditableField from '../../_forms/_components/EditableField'
import { EditableCalendar } from '../../_forms/_components/EditableCalendar'
import { EditableDropdown } from '../../_forms/_components/EditableDropdown'
import { OverlayPanel } from 'primereact/overlaypanel'
import BreakpointRender from '../../../../_core/components/breakpoint-render'
import DynamicButtonListSingle from '../../_forms/_components/DynamicButtonListSingle'
import DynamicButtonListMultiple from '../../_forms/_components/DynamicButtonListMultiple'
import PhotoUpload from '../../_components/profile/components/PhotoUpload'
import { ProfileVerify } from '../../_forms/_components/ProfileVerify'
import { Skeleton } from 'primereact/skeleton'
import { Dispatch, dispatcher, makeReducer, State, stateToR } from 'util/state-machine'
import Await from '../../../../_core/components/await'
import SubmitButton from '../../../../components/submit-button'

type FormState
  = Omit<OnboardingForm, 'Birthday' | 'latitude' | 'longitude' | 'Sex' | 'PreferredSex'>
  & { 'Sex':string | null }
  & { 'PreferredSex':string | null  }
  & { questions: Omit<UserQuestionResponse, 'toQuestion' | 'userId'>[] }
  & { Birthday: jsjoda.LocalDate | null }
  & { populated: boolean }

type SetState = <k extends keyof FormState>(key: k, value: FormState[k]) => void

let userPhotos = [] as UserPhoto[]

enum Action {
  LOADING = 'loading',
  SUBMIT = 'submit',

  GENERAL = 'general',
  NATIONALITY = 'nationality',
  GENDER = 'gender',
  GENDER_PREFERENCE = 'gender-preference',
  PHOTOS = 'photos',
  VERIFICATION = 'verification',

  SET = 'set',
}

type Which = 'goto' | 'set'

type StepActionData = { 'which':'goto','data':FormState } | { 'which':'set','data':Partial<{ [k in keyof FormState]:FormState[k] }> }

type Actions = {
  [Action.SUBMIT]:{ 'finish':() => void }

  [Action.GENERAL]          :StepActionData
  [Action.NATIONALITY]      :StepActionData
  [Action.GENDER]           :StepActionData
  // [Action.GENDER_PREFERENCE]:StepActionData
  [Action.PHOTOS]           :StepActionData
  [Action.VERIFICATION]     :StepActionData
}

type States = {
  [Action.SUBMIT]:null

  [Action.GENERAL]          :FormState
  [Action.NATIONALITY]      :FormState
  [Action.GENDER]           :FormState
  // [Action.GENDER_PREFERENCE]:FormState
  [Action.PHOTOS]           :FormState
  [Action.VERIFICATION]     :FormState
}

const reducer = makeReducer<States,Actions>((t) => [
  t(Action.SUBMIT           ,undefined               ,(a  ) => {
    a.data.finish()
    return {
      'stage':a.stage,
      'data':null,
    }
  }),

  t(Action.GENERAL          ,Action.GENERAL          ,(a,s) => ({ 'stage':a.stage,'data':a.data.which === 'goto' ? a.data.data : { ...s.data,...a.data.data }})),
  t(Action.GENERAL          ,undefined               ,(a  ) => {
    if(a.data.which === 'goto')
      return { 'stage':a.stage,'data':a.data.data }
    else
      throw 'SET in GENERAL when not in GENERAL'
  }),

  t(Action.NATIONALITY      ,Action.NATIONALITY      ,(a,s) => ({ 'stage':a.stage,'data':a.data.which === 'goto' ? s.data : { ...s.data,...a.data.data }})),
  t(Action.NATIONALITY      ,Action.GENERAL          ,(a,s) => ({ 'stage':a.stage,'data':a.data.which === 'goto' ? s.data : { ...s.data,...a.data.data }})),

  t(Action.GENDER           ,Action.GENDER           ,(a,s) => ({ 'stage':a.stage,'data':a.data.which === 'goto' ? s.data : { ...s.data,...a.data.data }})),
  t(Action.GENDER           ,Action.NATIONALITY      ,(a,s) => ({ 'stage':a.stage,'data':a.data.which === 'goto' ? s.data : { ...s.data,...a.data.data }})),

  t(Action.PHOTOS           ,Action.PHOTOS           ,(a,s) => ({ 'stage':a.stage,'data':a.data.which === 'goto' ? s.data : { ...s.data,...a.data.data }})),
  t(Action.PHOTOS           ,Action.GENDER           ,(a,s) => ({ 'stage':a.stage,'data':a.data.which === 'goto' ? s.data : { ...s.data,...a.data.data }})),

  t(Action.VERIFICATION     ,Action.VERIFICATION     ,(a,s) => ({ 'stage':a.stage,'data':a.data.which === 'goto' ? s.data : { ...s.data,...a.data.data }})),
  t(Action.VERIFICATION     ,Action.PHOTOS           ,(a,s) => ({ 'stage':a.stage,'data':a.data.which === 'goto' ? s.data : { ...s.data,...a.data.data }})),
])

const handleSubmit = async (
  e:React.FormEvent<HTMLFormElement>,
  onError:(_:{ [key: string]: string; }) => void,
  onNext:() => void,
  formdatas:IncomingFormPayload[]
) => {
  e.preventDefault()
  const results = await Promise.all(formdatas.map((formdata) => (async () => {
    try {
      const request = updateProfile(formdata)
      await request
      return undefined
    }
    catch (e) {
      if (isAxiosError(e)) {
        return getValidationErrors(e.response?.data)
      }
    }
  })()))
  const result = results.reduce((agg,x) => {
    if(x === undefined)
    {
      return agg
    }
    else
    {
      return { ...agg,...x }
    }
  },undefined)
  if(result === undefined)
  {
    onNext()
  }
  else
  {
    onError(result)
  }
}

type StepProps = {
  formstate: FormState
  setState: SetState
  onNext: () => void
}
type GeneralInformationProps = {

} & StepProps
type NationalitySelectionProps = {

} & StepProps
type GenderSelectionProps = {
  genders:Promise<EnumUserGendersData[]>
} & StepProps
type PhotosSubmissionProps = {

} & StepProps
type VerificationProps = {

} & StepProps

const GeneralInformation = ({ formstate, setState, onNext }:GeneralInformationProps) => {
  const { countryData } = useEnumOptions()
  const geocoder = useGeocoding()
  const location = useLocation(geocoder)
  const { openPopup } = React.useContext<PopUpContextType>(PopupContext)
  const [errors, setErrors] = React.useState<{ [key: string]: string }>({})
  const [stateSubmitting,setSubmitting] = React.useState(async () => {})

  const refFormStateLocation = React.useRef({
    'country': '',
    'latitude': 0,
    'longitude': 0,
  })

  React.useEffect(() => {
    (async () => {
      const location = await geocodeCountry(geocoder, formstate.country)
      refFormStateLocation.current = location ?? { 'country': '', 'latitude': 0, 'longitude': 0 }
    })()
  }, [formstate.country])

  const op = React.useRef<OverlayPanel>(null)
  return (
    <form className="mt-4" onSubmit={(e) => {
      setErrors({})
      setSubmitting(handleSubmit(e,(x) => {
        openPopup({
          'severity': 'error',
          'title': 'Form errors',
          'text': 'Please correct the highlighted items',
        })
        setErrors(x)
      },onNext,[
        {
          'FormType': FormType.BasicData,
          'Data': {
            ...formstate,
            'Birthday': formstate.Birthday?.toString() || '',
          },
        },
        {
          'FormType': FormType.LocationInfo,
          'Data': {
            ...formstate,
            ...(location ?? refFormStateLocation.current),
          },
        },
      ]))
    }}>
      <div className="d-flex flex-column gap-4">
        <EditableField
          id="FirstName"
          name="FirstName"
          label="First Name"
          value={formstate.FirstName}
          onChange={(x) => setState('FirstName', x)}
          errors={errors["firstName"]}
        />
        <span className='flex-column gap-4 mb-2'>
          <EditableField
            id="LastName"
            name="LastName"
            label="Last Name"
            value={formstate.LastName}
            onChange={(x) => setState('LastName', x)}
            errors={errors["lastName"]}
          />
          <span className="fw-light">We will only display your first name and first initial of your last name on your profile*</span>
        </span>
        <EditableCalendar
          id="Birthday"
          name="Birthday"
          label="Birthday"
          errors={errors["birthday"]}
          maxDate={jsjoda.LocalDate.now().plusYears(-18)}
          value={formstate.Birthday}
          onChange={(x) => setState('Birthday', x)}
        />
        {location
          ? <>Location: {location.country}</>
          : <>
            <div className="d-flex gap-3">
              <EditableDropdown
                containerClass="w-100" // TODO: to remove the w-50...?
                id="country"
                name="country"
                label="Location"
                errors={errors["country"]}
                value={formstate.country}
                onChange={(e) => setState('country',e.target.value)}
                options={countryData}
                optionLabel="display"
                optionValue="display"
                className="w-100"
                placeholder="Select your Country"
                filter
              />
              <div className="flex justify-content-center align-content-center">
                <span className="pi pi-exclamation-circle border-0" onClick={(e) => op.current?.toggle(e)} ></span>
                <OverlayPanel ref={op}>
                  <span className="fs-6 fw-light">Define your location to discover connections in your vicinity and beyond. Without this, matching with others won't be possible.</span>
                </OverlayPanel>
              </div>
            </div>
          </>
        }
        <div className="d-flex justify-content-end my-5">
          <SubmitButton dependency={stateSubmitting} label="Next"/>
        </div>
      </div>
    </form>
  )
}

const NationalitySelection = ({ formstate, setState, onNext }:NationalitySelectionProps) => {
  const { nationalityData } = useEnumOptions()
  const [errors, setErrors] = React.useState<{ [key: string]: string }>({})
  const [stateSubmitting,setSubmitting] = React.useState(async () => {})
  const { openPopup } = React.useContext<PopUpContextType>(PopupContext)

  return (
    <form onSubmit={(e) => {
      setErrors({})
      setSubmitting(handleSubmit(e,(x) => {
        openPopup({
          'severity': 'error',
          'title': 'Selection error',
          'text': `${errors["isoCode"]}`,
        })
        setErrors(x)
      },onNext,[
        {
          'FormType': FormType.Nationality,
          'Data': {
            ...formstate,
            "isoCode" : formstate.isoCode,
          },
        },
      ]))
    }}>
      <h2 className="d-flex justify-content-center mb-4">Select your nationalities</h2>
      <p className="text-center">What countries are you a citizen of? (Max 3)</p>
      <div className="d-flex flex-column gap-5">
        <DynamicButtonListMultiple
          value={nationalityData.filter(x => formstate.isoCode.includes(x.id))}
          onChange={(xs) => setState('isoCode',xs.map((x) => x.id))}
          primaryOptions={nationalityData.filter(x => !x.advancedOption)}
          secondaryOptions={nationalityData.filter(x => !!x.advancedOption)}
          optionLabel={(x) => x.display}
          optionLabelKey={(x) => x.id}
          maxSelection={3}
        />
      </div>
      <div className="d-flex justify-content-end my-5">
        <SubmitButton dependency={stateSubmitting} label="Next"/>
      </div>
    </form>
  )
}

const GenderSelections = ({ genders, formstate, setState, onNext }:GenderSelectionProps) => {
  const [errors, setErrors] = React.useState<{ [key: string]: string }>({})
  const [stateSubmitting,setSubmitting] = React.useState(async () => {})
  const { openPopup } = React.useContext<PopUpContextType>(PopupContext)
  return (
    <Await dependency={genders}>{(genders) =>
      <form onSubmit={(e) => {
        setErrors({})
        setSubmitting(handleSubmit(e, (x) => {
          openPopup({
            'severity': 'error',
            'title': 'Selection errors',
            'text': 'Please make a selection',
          })
          setErrors(x)
        }, onNext, [
          {
            'FormType': FormType.Sex,
            'Data': {
              ...formstate,
              "Sex": formstate.Sex!,
            },
          },
          {
            'FormType': FormType.DesiredSex,
            'Data': {
              ...formstate,
              "PreferredSex": formstate.PreferredSex!,
            },
          },
        ]))
      }}>
        <h2 className="d-flex justify-content-center mb-2">I am a</h2>
        <DynamicButtonListSingle
          value={genders.find(x => x.name == formstate.Sex) || null}
          onChange={(x) => setState('Sex', x && x.name)}
          primaryOptions={genders.filter(x => !x.advancedOption)}
          secondaryOptions={genders.filter(x => !!x.advancedOption)}
          optionLabel={(x) => x.display}
          optionLabelKey={(x) => x.name}
          vertical
        />
        <br />
        <br />
        <h2 className="d-flex justify-content-center mb-2">I'm looking to date</h2>
        <DynamicButtonListSingle
          value={genderPreferenceOptions.find((x) => x.id == formstate.PreferredSex) || null}
          onChange={(x) => setState('PreferredSex', x && x.id)}
          primaryOptions={genderPreferenceOptions}
          optionLabel={(x) => x.display}
          optionLabelKey={(x) => x.id}
          vertical
        />
        <div className="d-flex justify-content-end my-5">
          <SubmitButton dependency={stateSubmitting} label="Next"/>
        </div>
      </form>
    }
    </Await>
  )
}

const PhotosSubmission = ({ onNext }:PhotosSubmissionProps) => {
  const [stateSubmitting,setSubmitting] = React.useState(async () => {})
  const { openPopup } = React.useContext<PopUpContextType>(PopupContext)
  return (
    <form onSubmit={(e) => setSubmitting((async () => {
      e.preventDefault()
      if(userPhotos.length < 1)
      {
        openPopup({ severity: 'error', title: 'A photo is required', text: 'One photo is required', duration: 3000 })
      }
      else
      {
        onNext()
      }
    })())}>
      <h2 className="d-flex justify-content-center mb-4">Photos</h2>
      <PhotoUpload
        key="photo-upload"
        onLoad={(images) => { userPhotos = images }}
        onChange={(images) => { userPhotos = images }}
      />
      <div className="d-flex justify-content-end my-5">
        <SubmitButton dependency={stateSubmitting} label="Next"/>
      </div>
    </form>
  )
}

const Verification = ({ onNext }:VerificationProps) => {
  const [visible, setVisible] = React.useState(false)

  return (
    <form>
      {visible ?
        (<div>
          <span className="d-flex pi pi-times mx-2 justify-content-end" onClick={() => { setVisible(false) }}></span>
          <ProfileVerify
            onClose={() => {
              setVisible(false)
              // reloadCurrentUser() // TODO: fix this
              onNext()
            }}
          />
        </div>)
        :
        (<div className="d-flex flex-column gap-4">
          <h2 className="text-center">To get verified</h2>
          {/* <h5 className="d-flex gap-4"> copy the pose presented and take a photo matching the pose, we'll check to make sure the photos match.</h5> */}
          <h5 className="gap-4 text-center">A pose will be presented. <br /> Take a photo matching the pose. <br /> Our team will check if the photos match.</h5>
		  
          <div className="d-flex justify-content-center">
            <button type="button" className="align-items-center btn btn-primary" onClick={() => { setVisible(true) }}>
              Take my photo <span className="pi pi-camera mx-2"></span>
            </button>
          </div>
		   <p style={{ fontSize: '12pt', textAlign: 'center', marginTop: '1rem' }}>
			If your browser blocks the camera and doesn't ask your permission when clicking the button above, check these{' '}
			<a href="https://secure.datingacrossthepond.com/support.html">
			  instructions
			</a>{' '}.
		  </p>
          <p className="text-muted" style={{ textAlign: 'center'}}>Pictures are only to be used to verify your identity
            and will not be posted on your account. For more information on how we use,
            retain, and protect your personal data, please read our <a href="/privacy-policy">Privacy Policy</a>.
          </p>
        </div>)
      }
    </form>
  )
}

const Content = ({ genders,state, dispatch }:{ genders:Promise<EnumUserGendersData[]>, state:State<States>, dispatch:Dispatch<Actions> }) : React.ReactNode => {
  const navigate = useNavigate()
  const setState = <k extends keyof FormState>(k:k,v:FormState[k]) => {
    dispatch(state.stage,{ 'which':'set','data':{ [k]:v } })
  }
  return stateToR(state,{
    [Action.SUBMIT]:(data) => <></>,
    [Action.GENERAL]:(data) =>
      data.populated
      ? <GeneralInformation formstate={data} setState={setState} onNext={() => dispatch(Action.NATIONALITY,{ 'which':'goto','data':data })}></GeneralInformation>
      : <Skeleton></Skeleton>
    ,
    [Action.NATIONALITY]:(data) =>
      <NationalitySelection formstate={data} setState={setState} onNext={() => dispatch(Action.GENDER,{ 'which':'goto','data':data })}></NationalitySelection>
    ,
    [Action.GENDER]:(data) =>
      <GenderSelections genders={genders} formstate={data} setState={setState} onNext={() => dispatch(Action.PHOTOS,{ 'which':'goto','data':data })}></GenderSelections>
    ,
    [Action.PHOTOS]:(data) =>
      <PhotosSubmission formstate={data} setState={setState} onNext={() => dispatch(Action.VERIFICATION,{ 'which':'goto','data':data })}></PhotosSubmission>
    ,
    [Action.VERIFICATION]:(data) =>
      <Verification formstate={data} setState={setState} onNext={() => dispatch(Action.SUBMIT,{ 'finish':() => navigate('/discover')})}></Verification>
    ,
  })
}

const Onboarding = () => {
  const [currentUser, reloadCurrentUser] = useCurrentUser()
  const [genders, _] = useFetchV2<EnumUserGendersData[],undefined>(async () => (await getGendersEnum()).data.sort((x: EnumUserGendersData) => x.advancedOption ? 1 : -1), undefined, [])
  const [state, dispatch] = React.useReducer(reducer,{
    'stage':Action.GENERAL,
    'data':{
      'populated': false,
      'FirstName': '',
      'LastName': '',
      'Email': '',
      'Birthday': null,
      'PreferredSex': '',
      'HighestLevel': '',
      'HasOrWantsKids': '',
      'SmokeTobacco': '',
      'DrinkAlcohol': '',
      'SmokeWeed': '',
      'OccupationName': '',
      'inputText': '',
      'Sex': '',
      'ReligiousBelief': '',
      'PoliticalBelief': '',
      'measurement': null,
      'bodyType': '',
      'isoCode': [],
      'country': '',
      'interests': [],
      'questions': [],
    }
  })
  React.useEffect(() => {
    let ignore = false
    currentUser.then((x) => {
      if(!ignore)
      {
        dispatch({
          'stage':Action.GENERAL,
          'data':{
            'which':'set',
            'data':{
              'populated':true,
              'FirstName': x.firstName,
              'LastName': x.lastName,
              // 'Email': x.email,
              'Birthday': !!x.userMetadata && !!x.userMetadata.birthday ? jsjoda.LocalDate.parse(x.userMetadata.birthday) : null,
              'PreferredSex': !!x.userMetadata && !!x.userMetadata.preferredSex ? x.userMetadata.preferredSex : '',
              // 'HighestLevel': !!x.userMetadata && !!x.userMetadata.highestEducationLevel ? x.userMetadata.highestEducationLevel : '',
              // 'HasOrWantsKids': !!x.userMetadata && !!x.userMetadata.hasOrWantsKids ? x.userMetadata.hasOrWantsKids : '',
              // 'SmokeTobacco': !!x.userMetadata && !!x.userMetadata.smokesTobacco ? x.userMetadata.smokesTobacco : '',
              // 'DrinkAlcohol': !!x.userMetadata && !!x.userMetadata.drinksAlcohol ? x.userMetadata.drinksAlcohol : '',
              // 'SmokeWeed': !!x.userMetadata && !!x.userMetadata.smokesWeed ? x.userMetadata.smokesWeed : '',
              // 'OccupationName': !!x.userMetadata && !!x.userMetadata.occupationName ? x.userMetadata.occupationName : '',
              // 'inputText': !!x.userMetadata && !!x.userMetadata.aboutMe ? x.userMetadata.aboutMe : '',
              'Sex': !!x.userMetadata && !!x.userMetadata.sexName ? x.userMetadata.sexName : '',
              // 'ReligiousBelief': !!x.userMetadata && !!x.userMetadata.religiousBeliefName ? x.userMetadata.religiousBeliefName : '',
              // 'PoliticalBelief': !!x.userMetadata && !!x.userMetadata.politicalBeliefName ? x.userMetadata.politicalBeliefName : '',
              // 'measurement': !!x.userMetadata && !!x.userMetadata.heightMeasurement ? x.userMetadata.heightMeasurement : null,
              // 'bodyType': !!x.userMetadata && !!x.userMetadata.bodyTypeName ? x.userMetadata.bodyTypeName : '',
              'isoCode': !!x.userMetadata && !!x.userMetadata.Nationalities ? x.userMetadata.Nationalities.map((x) => x.nationalityIso) : [],
            },
          },
        })
      }
    })
    return () => { ignore = true }
  },[currentUser])

  return (
    <>
      <BreakpointRender eq="desktop">
        <div className="justify-content-center align-content-center h-100 w-40 m-auto">
          <Content genders={genders} state={state} dispatch={dispatcher(dispatch)}></Content>
        </div>
      </BreakpointRender>
      <BreakpointRender eq="mobile">
        <div className="justify-content-center align-content-center h-100 w-90 m-auto">
          <Content genders={genders} state={state} dispatch={dispatcher(dispatch)}></Content>
        </div>
      </BreakpointRender>
    </>
  )
}

export default Onboarding
